import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  FlatList,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Image,
  ImageBackground,
  StatusBar,
  Keyboard,
  Animated,
  Alert,
  Modal,
  ScrollView,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { observer } from '@legendapp/state/react';
import { Tables } from '../utils/database.types';
import { Ionicons } from '@expo/vector-icons';
import { getCurrentUser } from '../utils/Firebase';
import { getOrCreateSession, getMessagesForSession } from '../utils/Supa';
import {
  appState$,
  setSpicyEnabled,
  canSendMessage,
  incrementMessageCount,
  getRemainingMessages,
  getDailyMessageLimit,
  isSubscribed,
  getRelationshipLevel,
  addAffection,
  spendCoins,
  addCoins,
} from '../utils/AppState';
import { getGiftTypes, sendGift } from '../utils/subscriptionHelpers';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  imageUrl?: string;
}

interface ChatScreenProps {
  character: Tables<'characters'>;
  onBack: () => void;
}

const MessageBubble = observer(({ message, accentColor }: { message: Message; accentColor: string }) => {
  const isUser = message.role === 'user';
  const hasImage = !!message.imageUrl;

  return (
    <View style={[styles.messageContainer, isUser ? styles.userMessage : styles.aiMessage]}>
      <View style={[
        styles.bubble,
        isUser ? styles.userBubble : styles.aiBubble,
        !isUser && { borderColor: `${accentColor}55` },
        hasImage && styles.imageBubble,
      ]}>
        {hasImage ? (
          <Image
            source={{ uri: message.imageUrl }}
            style={styles.messageImage}
            resizeMode="cover"
          />
        ) : (
          <Text style={[styles.messageText, isUser ? styles.userText : styles.aiText]}>
            {message.content}
          </Text>
        )}
      </View>
    </View>
  );
});

const TypingIndicator = observer(({ accentColor }: { accentColor: string }) => {
  return (
    <View style={[styles.typingContainer, { borderColor: `${accentColor}44` }]}>
      <View style={styles.typingDots}>
        <View style={[styles.dot, styles.dot1]} />
        <View style={[styles.dot, styles.dot2]} />
        <View style={[styles.dot, styles.dot3]} />
      </View>
    </View>
  );
});

const ChatScreen = observer(({ character, onBack }: ChatScreenProps) => {
  const insets = useSafeAreaInsets();
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [giftModalVisible, setGiftModalVisible] = useState(false);
  const [giftTypes, setGiftTypes] = useState<Tables<'gift_types'>[]>([]);
  const [relationship, setRelationship] = useState({ level: 1, affectionScore: 0 });
  const flatList = React.useRef<FlatList>(null);
  const spicyEnabled = appState$.spicyEnabled.get();

  const keyboardHeight = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (Platform.OS === 'web') return; // Keyboard API doesn't work reliably on web

    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSubscription = Keyboard.addListener(showEvent, (event) => {
      Animated.timing(keyboardHeight, {
        duration: event.duration || 200,
        toValue: event.endCoordinates.height,
        useNativeDriver: false,
      }).start();
    });

    const hideSubscription = Keyboard.addListener(hideEvent, (event) => {
      Animated.timing(keyboardHeight, {
        duration: event.duration || 200,
        toValue: 0,
        useNativeDriver: false,
      }).start();
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  const handleRequestPhoto = async () => {
    if (isGeneratingImage) return;

    const PHOTO_COST = 100;
    const currentCoins = appState$.coins.get();
    if (currentCoins < PHOTO_COST) {
      const msg = `You need ${PHOTO_COST} coins to request a photo.`;
      if (Platform.OS === 'web') {
        window.alert(`Not Enough Coins\n${msg}`);
      } else {
        Alert.alert('Not Enough Coins', msg);
      }
      return;
    }

    setIsGeneratingImage(true);

    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        Alert.alert('Error', 'You must be logged in to request photos.');
        setIsGeneratingImage(false);
        return;
      }

      const response = await fetch('https://ai-chat.virtualhalf.workers.dev/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': currentUser.uid,
        },
        body: JSON.stringify({
          characterId: character.id,
          sessionId,
          spicy: appState$.spicyEnabled.get(),
          coins: appState$.coins.get(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        Alert.alert('Error', errorData.error || 'Failed to generate image');
        setIsGeneratingImage(false);
        return;
      }

      const data = await response.json();
      const imageUrl = data.imageUrl;

      // Update local coin count from worker response
      if (typeof data.coins === 'number') {
        appState$.coins.set(data.coins);
      }

      const imageMessage: Message = {
        id: Date.now().toString(),
        role: 'assistant',
        content: 'Here is a photo for you 💕',
        created_at: new Date().toISOString(),
        imageUrl,
      };

      setMessages((prev) => [...prev, imageMessage]);

      // Worker saves the image message to DB; no need for client-side save
    } catch (error) {
      console.error('[ERROR] Request photo error:', error);
      Alert.alert('Error', 'Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    if (!canSendMessage()) {
      const remaining = getRemainingMessages();
      const limit = getDailyMessageLimit();
      Alert.alert(
        'Message Limit Reached',
        `Free users can send ${limit} messages per day. You have ${remaining} remaining.\n\nUpgrade to Premium for unlimited messages!`,
        [
          { text: 'OK', style: 'cancel' },
          { text: 'Upgrade', onPress: () => Alert.alert('Coming Soon', 'Subscription upgrade will be available soon!') },
        ]
      );
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input.trim(),
      created_at: new Date().toISOString(),
    };

    incrementMessageCount();
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    try {
      // Get current user ID from Firebase
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('User not authenticated');
      }

      // Call Cloudflare Edge Function
      const response = await fetch('https://ai-chat.virtualhalf.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-ID': currentUser.uid,
        },
        body: JSON.stringify({
          characterId: character.id,
          currentMessage: userMessage.content,
          messageHistory: messages.map(msg => ({
            role: msg.role,
            content: msg.content,
          })),
          sessionId: sessionId,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ERROR] Response body:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Create AI message placeholder for streaming
      const aiMessageId = (Date.now() + 1).toString();
      const aiMessage: Message = {
        id: aiMessageId,
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
      };

      setMessages(prev => [...prev, aiMessage]);

      // Try to stream the response, fall back to non-streaming if not available
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      let fullResponse = '';

      if (reader) {
        // Streaming is available
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          fullResponse += chunk;

          // Update the AI message content as it streams
          setMessages(prev =>
            prev.map(msg =>
              msg.id === aiMessageId
                ? { ...msg, content: fullResponse }
                : msg
            )
          );
        }
      } else {
        // Fallback: Read entire response as text (for Android/React Native compatibility)
        fullResponse = await response.text();
        setMessages(prev =>
          prev.map(msg =>
            msg.id === aiMessageId
              ? { ...msg, content: fullResponse }
              : msg
          )
        );
      }

      setIsTyping(false);

      // Increment affection for successful chat
      addAffection(character.id, 2);
      setRelationship(getRelationshipLevel(character.id));

      // Worker saves both user and AI messages to Supabase; no client-side save needed

    } catch (error) {
      console.error('[ERROR] Error sending message:', error);
      setIsTyping(false);

      // Add error message
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        created_at: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  useEffect(() => {
    const loadSessionAndMessages = async () => {
      try {
        const newSessionId = await getOrCreateSession(character.id);

        if (newSessionId) {
          setSessionId(newSessionId);
          const loadedMessages = await getMessagesForSession(newSessionId);
          setMessages(loadedMessages);
        }
      } catch (error) {
        console.error('[ChatScreen] Error loading session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const loadGiftTypes = async () => {
      const types = await getGiftTypes();
      setGiftTypes(types);
    };

    setRelationship(getRelationshipLevel(character.id));
    loadSessionAndMessages();
    loadGiftTypes();
  }, [character.id]);

  const handleSendGift = async (gift: Tables<'gift_types'>) => {
    if (!spendCoins(gift.coin_cost)) {
      const msg = `You need ${gift.coin_cost} coins to send this gift.`;
      if (Platform.OS === 'web') {
        window.alert(`Not Enough Coins\n${msg}`);
      } else {
        Alert.alert('Not Enough Coins', msg);
      }
      return;
    }
    const sent = await sendGift(character.id, gift.id);
    if (sent) {
      addAffection(character.id, gift.affection_boost);
      setRelationship(getRelationshipLevel(character.id));
      Alert.alert('Gift Sent!', `${character.name} loved your ${gift.name}! 💕`);
    } else {
      addCoins(gift.coin_cost); // refund
      Alert.alert('Error', 'Failed to send gift. Please try again.');
    }
    setGiftModalVisible(false);
  };

  const renderMessage = ({ item }: { item: Message }) => (
    <MessageBubble message={item} accentColor={character.accent_color} />
  );

  const chatContent = (
    <>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />

      <ImageBackground
        source={character.avatar_url ? { uri: character.avatar_url } : undefined}
        style={styles.background}
        resizeMode="cover"
      >
        <View style={styles.backdrop}>
          <View style={[styles.header, { marginTop: insets.top + 6 }]}>
            <TouchableOpacity onPress={onBack} style={styles.backButton}>
              <Ionicons name="chevron-back" size={24} color="#F9FAFB" />
            </TouchableOpacity>

            {character.avatar_url ? (
              <Image source={{ uri: character.avatar_url }} style={styles.headerAvatarImage} />
            ) : (
              <View style={[styles.headerAvatarPlaceholder, { backgroundColor: character.accent_color }]}>
                <Text style={styles.headerAvatarText}>{character.name[0]}</Text>
              </View>
            )}

            <View style={styles.headerInfo}>
              <Text style={styles.headerName}>{character.name}</Text>
              <View style={styles.relationshipBadge}>
                <Ionicons name="heart" size={12} color="#FF2CA8" />
                <Text style={styles.relationshipText}>
                  Lvl {relationship.level} · {relationship.affectionScore}/100
                </Text>
              </View>
            </View>

            <TouchableOpacity
              style={styles.menuButton}
              onPress={() =>
                Alert.alert(
                  'Chat Options',
                  '',
                  [
                    { text: 'Clear Chat', onPress: () => setMessages([]) },
                    { text: 'Report', onPress: () => Alert.alert('Reported', 'Thank you for your feedback.') },
                    { text: 'Cancel', style: 'cancel' },
                  ]
                )
              }
            >
              <Ionicons name="ellipsis-vertical" size={18} color="#F9FAFB" />
            </TouchableOpacity>
          </View>

          <FlatList
            data={messages}
            renderItem={renderMessage}
            keyExtractor={(item) => item.id}
            style={styles.messagesList}
            contentContainerStyle={styles.messagesContent}
            onContentSizeChange={(w, h) => h && flatList.current?.scrollToEnd({ animated: true })}
            onLayout={() => flatList.current?.scrollToEnd({ animated: true })}
            ref={flatList}
            ListEmptyComponent={
              isLoading ? (
                <View style={styles.loadingContainer}>
                  <Text style={styles.loadingText}>Loading chat history...</Text>
                </View>
              ) : null
            }
          />

          {isTyping && <TypingIndicator accentColor={character.accent_color} />}

          <View style={styles.actionRow}>
            <TouchableOpacity
              style={styles.quickAction}
              onPress={() => setGiftModalVisible(true)}
            >
              <Text style={styles.quickActionText}>🎁 Gifts</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickAction}
              onPress={handleRequestPhoto}
              disabled={isGeneratingImage}
            >
              <Text style={[styles.quickActionText, isGeneratingImage && { opacity: 0.5 }]}>
                {isGeneratingImage ? '⏳ Generating...' : '📸 Req Photo'}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.quickAction}
              onPress={() => setSpicyEnabled(!spicyEnabled)}
            >
              <Text style={styles.quickActionText}>😈 Spicy Chats</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.togglePill, spicyEnabled && styles.togglePillActive]}
              onPress={() => setSpicyEnabled(!spicyEnabled)}
            >
              <View style={[styles.toggleKnob, spicyEnabled && styles.toggleKnobActive]} />
            </TouchableOpacity>
          </View>
        </View>

        <View style={[
          styles.inputContainer,
          { paddingBottom: Platform.OS === 'ios' && insets.bottom > 0 ? insets.bottom : Platform.OS === 'android' ? 35 : 20 }
        ]}>
          <View style={{ flex: 1 }}>
            <TextInput
              value={input}
              onChangeText={setInput}
              placeholder="Type a message..."
              placeholderTextColor="#6B7280"
              style={styles.input}
              returnKeyType="send"
              onSubmitEditing={handleSend}
              blurOnSubmit={false}
            />
            {!isSubscribed() && (
              <Text style={styles.quotaText}>
                {getRemainingMessages()} / {getDailyMessageLimit()} messages left today
              </Text>
            )}
          </View>
          <TouchableOpacity
            onPress={handleSend}
            style={[styles.sendButton, !input.trim() && styles.sendButtonDisabled]}
            disabled={!input.trim()}
          >
            <Ionicons
              name="send"
              size={24}
              color={input.trim() ? '#F9FAFB' : '#6B7280'}
            />
          </TouchableOpacity>
        </View>
      </ImageBackground>

      {/* Gift Picker Modal */}
      <Modal
        animationType="slide"
        transparent
        visible={giftModalVisible}
        onRequestClose={() => setGiftModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Send a Gift 💝</Text>
            <ScrollView showsVerticalScrollIndicator={false}>
              {giftTypes.map((gift) => (
                <TouchableOpacity
                  key={gift.id}
                  style={styles.giftOption}
                  onPress={() => handleSendGift(gift)}
                  activeOpacity={0.8}
                >
                  <View style={styles.giftIconWrap}>
                    <Ionicons name={gift.icon as keyof typeof Ionicons.glyphMap} size={24} color="#FF2CA8" />
                  </View>
                  <View style={styles.giftInfo}>
                    <Text style={styles.giftName}>{gift.name}</Text>
                    <Text style={styles.giftBoost}>+{gift.affection_boost} affection</Text>
                  </View>
                  <View style={styles.giftCost}>
                    <Ionicons name="logo-bitcoin" size={14} color="#FFCA38" />
                    <Text style={styles.giftCostText}>{gift.coin_cost}</Text>
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <TouchableOpacity
              style={styles.modalClose}
              onPress={() => setGiftModalVisible(false)}
            >
              <Text style={styles.modalCloseText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </>
  );

  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        {chatContent}
      </View>
    );
  }

  return (
    <Animated.View style={[styles.container, { paddingBottom: keyboardHeight }]}>
      {chatContent}
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#090B1B',
    overflow: 'hidden',
  },
  background: {
    flex: 1,
    overflow: 'hidden',
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(7, 10, 25, 0.4)',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  backButton: {
    padding: 4,
    marginRight: 8,
  },
  headerAvatarImage: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.7)',
  },
  headerAvatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerAvatarText: {
    fontSize: 20,
    fontWeight: '500',
    color: '#F9FAFB',
  },
  headerName: {
    fontSize: 34,
    fontWeight: '700',
    color: '#F9FAFB',
  },
  menuButton: {
    padding: 8,
    marginLeft: 8,
  },
  messagesList: {
    flex: 1,
  },
  messagesContent: {
    padding: 14,
    paddingBottom: 10,
    justifyContent: 'flex-end',
    minHeight: '70%',
  },
  messageContainer: {
    marginBottom: 12,
    maxWidth: '80%',
  },
  userMessage: {
    alignSelf: 'flex-end',
  },
  aiMessage: {
    alignSelf: 'flex-start',
  },
  bubble: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 16,
    borderWidth: 1,
    maxWidth: '100%',
  },
  userBubble: {
    backgroundColor: '#B748FF',
    borderColor: 'rgba(255,255,255,0.2)',
    borderBottomRightRadius: 4,
  },
  aiBubble: {
    backgroundColor: 'rgba(23, 29, 58, 0.92)',
    borderBottomLeftRadius: 4,
  },
  messageText: {
    fontSize: 15,
    lineHeight: 20,
  },
  userText: {
    color: '#F9FAFB',
  },
  aiText: {
    color: '#F9FAFB',
  },
  typingContainer: {
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginLeft: 16,
    marginBottom: 10,
    backgroundColor: 'rgba(23, 29, 58, 0.92)',
    borderWidth: 1,
    borderRadius: 16,
    borderBottomLeftRadius: 4,
    maxWidth: 90,
  },
  typingDots: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#C85CFF',
    marginHorizontal: 2,
  },
  dot1: {
    opacity: 0.3,
  },
  dot2: {
    opacity: 0.6,
  },
  dot3: {
    opacity: 1,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    marginBottom: 8,
  },
  quickAction: {
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderColor: 'rgba(255,255,255,0.24)',
    borderWidth: 1,
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  quickActionText: {
    color: '#F5F7FF',
    fontSize: 13,
    fontWeight: '500',
  },
  togglePill: {
    width: 38,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.35)',
    backgroundColor: 'rgba(255,255,255,0.22)',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  togglePillActive: {
    backgroundColor: '#B748FF',
    borderColor: '#B748FF',
  },
  toggleKnob: {
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#FFFFFF',
    alignSelf: 'flex-start',
  },
  toggleKnobActive: {
    alignSelf: 'flex-end',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingTop: 8,
    backgroundColor: 'transparent',
  },
  input: {
    flex: 1,
    backgroundColor: 'rgba(18, 22, 46, 0.95)',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    paddingHorizontal: 16,
    paddingVertical: 12,
    color: '#F9FAFB',
    fontSize: 16,
    maxHeight: 100,
    marginRight: 10,
    minHeight: 48,
  },
  sendButton: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#B748FF',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#B748FF',
    shadowOpacity: 0.45,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 4 },
  },
  sendButtonDisabled: {
    backgroundColor: '#242428',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    color: '#6B7280',
    fontSize: 14,
  },
  imageBubble: {
    padding: 4,
    overflow: 'hidden',
  },
  messageImage: {
    width: 220,
    height: 220,
    borderRadius: 14,
    backgroundColor: '#1a1a2e',
  },
  headerInfo: {
    flex: 1,
    marginLeft: 10,
  },
  relationshipBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  relationshipText: {
    color: '#FF2CA8',
    fontSize: 12,
    fontWeight: '600',
  },
  quotaText: {
    color: '#8D93B3',
    fontSize: 11,
    marginTop: 4,
    marginLeft: 4,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#191D33',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    padding: 20,
    maxHeight: '70%',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
  },
  modalTitle: {
    color: '#F5F7FF',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 16,
    textAlign: 'center',
  },
  giftOption: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#12152B',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  giftIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,44,168,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  giftInfo: {
    flex: 1,
  },
  giftName: {
    color: '#F5F7FF',
    fontSize: 15,
    fontWeight: '600',
  },
  giftBoost: {
    color: '#FF2CA8',
    fontSize: 12,
    marginTop: 2,
  },
  giftCost: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(255,202,56,0.12)',
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  giftCostText: {
    color: '#FFCA38',
    fontSize: 14,
    fontWeight: '700',
  },
  modalClose: {
    marginTop: 12,
    alignSelf: 'center',
    paddingVertical: 12,
    paddingHorizontal: 40,
    borderRadius: 20,
    backgroundColor: '#3A274D',
  },
  modalCloseText: {
    color: '#F5F7FF',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default ChatScreen;
